// Sprint 6 — Reservas Recurrentes

import { useEffect, useMemo, useState } from "react";
import { css } from "styled-system/css";
import { Plus } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

type ClientListRow = {
  clientId: string | null;
  email: string;
  displayName: string;
  phone: string | null;
};

type ServiceOpt = { id: string; name: string; duration: number };

const DOW_OPTIONS = [
  { v: 0, label: "Domingo" },
  { v: 1, label: "Lunes" },
  { v: 2, label: "Martes" },
  { v: 3, label: "Miércoles" },
  { v: 4, label: "Jueves" },
  { v: 5, label: "Viernes" },
  { v: 6, label: "Sábado" },
];

export function CreateRecurringModal({
  open,
  onClose,
  professionalQuery,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  professionalQuery: string;
  onCreated: (result: { created: number; skipped: number; total: number }) => void;
}) {
  const { refresh } = useAuth();
  const [clients, setClients] = useState<ClientListRow[]>([]);
  const [services, setServices] = useState<ServiceOpt[]>([]);
  const [clientSource, setClientSource] = useState<"crm" | "manual">("crm");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [frequency, setFrequency] = useState<"weekly" | "biweekly" | "monthly">("weekly");
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [startTime, setStartTime] = useState("10:00");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMsg(null);
    void (async () => {
      const [rC, rS] = await Promise.all([
        fetchWithSessionSync(`/api/dashboard/clients${professionalQuery}`, { credentials: "include" }, refresh),
        fetchWithSessionSync(`/api/dashboard/services${professionalQuery}`, { credentials: "include" }, refresh),
      ]);
      if (rC.ok) setClients((await rC.json()) as ClientListRow[]);
      if (rS.ok) {
        const list = (await rS.json()) as ServiceOpt[];
        setServices(list);
        if (!serviceId && list[0]?.id) setServiceId(list[0].id);
      }
    })();
  }, [open, refresh, professionalQuery]);

  useEffect(() => {
    if (!open) return;
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    if (!startDate) setStartDate(`${y}-${m}-${d}`);
  }, [open, startDate]);

  const crmClients = useMemo(
    () => clients.filter((c) => c.clientId && `${c.displayName} ${c.email}`.toLowerCase().includes(clientSearch.toLowerCase())),
    [clients, clientSearch],
  );

  useEffect(() => {
    if (clientSource !== "crm" || !selectedClientId) return;
    const c = clients.find((x) => x.clientId === selectedClientId);
    if (c) {
      setClientName(c.displayName);
      setClientEmail(c.email);
      setClientPhone(c.phone ?? "");
    }
  }, [clientSource, selectedClientId, clients]);

  async function submit() {
    if (!serviceId || !startDate) return;
    if (clientSource === "manual" && (!clientName.trim() || !clientEmail.trim())) {
      setMsg("Completá nombre y email del cliente");
      return;
    }
    if (clientSource === "crm" && !selectedClientId) {
      setMsg("Seleccioná un cliente del CRM o usá entrada manual");
      return;
    }
    if (frequency === "weekly" && (dayOfWeek < 0 || dayOfWeek > 6)) {
      setMsg("Seleccioná día de la semana");
      return;
    }

    setBusy(true);
    setMsg(null);
    const body = {
      clientId: clientSource === "crm" ? selectedClientId : null,
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim(),
      clientPhone: clientPhone.trim() || null,
      serviceId,
      frequency,
      dayOfWeek: frequency === "weekly" ? dayOfWeek : null,
      startTime,
      startDate,
      endDate: endDate.trim() || null,
    };
    const r = await fetchWithSessionSync(
      `/api/dashboard/recurring-bookings${professionalQuery}`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
      refresh,
    );
    setBusy(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setMsg(j.error ?? "No se pudo crear");
      return;
    }
    const j = (await r.json()) as { created: number; skipped: number; total: number };
    onCreated(j);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="Nueva reserva recurrente">
      {msg ? <p className={css({ fontSize: "sm", color: "#b91c1c", mb: "3" })}>{msg}</p> : null}
      <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
        <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
          <Button type="button" variant={clientSource === "crm" ? "primary" : "secondary"} onClick={() => setClientSource("crm")}>
            Cliente CRM
          </Button>
          <Button type="button" variant={clientSource === "manual" ? "primary" : "secondary"} onClick={() => setClientSource("manual")}>
            Manual
          </Button>
        </div>

        {clientSource === "crm" ? (
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Buscar cliente</span>
            <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Nombre o email" />
            <select
              className={css({ padding: "10px 12px", borderRadius: "10px", border: "1px solid #e2e8f0" })}
              value={selectedClientId ?? ""}
              onChange={(e) => setSelectedClientId(e.target.value || null)}
            >
              <option value="">Elegí un contacto con perfil…</option>
              {crmClients.map((c) => (
                <option key={c.clientId!} value={c.clientId!}>
                  {c.displayName} · {c.email}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Nombre</span>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} />
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Email</span>
              <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} />
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Teléfono</span>
              <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} />
            </label>
          </>
        )}

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Servicio</span>
          <select
            className={css({ padding: "10px 12px", borderRadius: "10px", border: "1px solid #e2e8f0" })}
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
          >
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {s.duration} min
              </option>
            ))}
          </select>
        </label>

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Frecuencia</span>
          <select
            className={css({ padding: "10px 12px", borderRadius: "10px", border: "1px solid #e2e8f0" })}
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as "weekly" | "biweekly" | "monthly")}
          >
            <option value="weekly">Semanal</option>
            <option value="biweekly">Quincenal (cada 14 días)</option>
            <option value="monthly">Mensual (mismo día del mes)</option>
          </select>
        </label>

        {frequency === "weekly" ? (
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Día de la semana</span>
            <select
              className={css({ padding: "10px 12px", borderRadius: "10px", border: "1px solid #e2e8f0" })}
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(Number(e.target.value))}
            >
              {DOW_OPTIONS.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {frequency === "biweekly" ? (
          <p className={css({ fontSize: "xs", color: "#64748b", lineHeight: "1.5" })}>
            Las fechas se calculan cada 14 días desde la fecha de inicio (ancla), sin depender del día de la semana del selector.
          </p>
        ) : null}

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Hora</span>
          <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
        </label>

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Fecha de inicio</span>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Fecha de fin (opcional)</span>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>

        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end" })}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={busy || !serviceId || !startDate}
            onClick={() => void submit()}
            className={css({ display: "inline-flex", alignItems: "center", gap: "2" })}
          >
            <Plus size={18} strokeWidth={2} aria-hidden />
            {busy ? "Creando…" : "Crear serie"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export type RecurringRow = {
  id: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string | null;
  frequencyLabel: string;
  startTime: string;
  nextOccurrenceAt: string | null;
  isActive: boolean;
  endDate: string | null;
};

export function EditRecurringModal({
  open,
  onClose,
  row,
  professionalQuery,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  row: RecurringRow | null;
  professionalQuery: string;
  onSaved: () => void;
}) {
  const { refresh } = useAuth();
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [endDate, setEndDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!row || !open) return;
    setClientName(row.clientName);
    setClientEmail(row.clientEmail);
    setClientPhone(row.clientPhone ?? "");
    setEndDate(row.endDate ? row.endDate.slice(0, 10) : "");
    setMsg(null);
  }, [row, open]);

  async function save() {
    if (!row) return;
    setBusy(true);
    setMsg(null);
    const body: Record<string, unknown> = {
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim(),
      clientPhone: clientPhone.trim() || null,
      endDate: endDate.trim() ? endDate.trim() : null,
    };
    const r = await fetchWithSessionSync(
      `/api/dashboard/recurring-bookings/${row.id}${professionalQuery}`,
      { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
      refresh,
    );
    setBusy(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setMsg(j.error ?? "No se pudo guardar");
      return;
    }
    onSaved();
    onClose();
  }

  if (!row) return null;

  return (
    <Modal open={open} onClose={onClose} title="Editar serie recurrente">
      {msg ? <p className={css({ fontSize: "sm", color: "#b91c1c", mb: "3" })}>{msg}</p> : null}
      <p className={css({ fontSize: "xs", color: "#64748b", mb: "3" })}>
        Solo podés actualizar datos de contacto y fecha de fin. La regla de recurrencia no se modifica.
      </p>
      <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Nombre</span>
          <Input value={clientName} onChange={(e) => setClientName(e.target.value)} />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Email</span>
          <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Teléfono</span>
          <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Fecha de fin (vacío = sin fin)</span>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end" })}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={busy || !clientName.trim() || !clientEmail.trim()} onClick={() => void save()}>
            {busy ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
